import { Link } from "react-router-dom";
import { ArrowUpRight } from "@phosphor-icons/react";
import { useServices } from "@/contexts/ServicesContext";
import { useSettings } from "@/contexts/SettingsContext";
import { CalendlyButton } from "@/components/CalendlyButton";
import { BrandLogo } from "@/components/BrandLogo";
import { FooterLinkColumn } from "@/components/FooterLinkColumn";
import { SocialIcons } from "@/components/SocialIcons";
import { useHeaderConfig } from "@/hooks/useHeaderConfig";

const formatCopyright = (template) => (template || "").replace("{year}", new Date().getFullYear());

export const Footer = () => {
  const { services } = useServices();
  const settings = useSettings();
  const { navStructure } = useHeaderConfig();
  const devServices = services.filter((s) => s.category === "development");
  const marketingServices = services.filter((s) => s.category === "marketing");

  const navLinks = navStructure.map((l) => ({ path: l.path, label: l.label, testId: l.label.toLowerCase() }));
  const devLinks = devServices.map((s) => ({ path: `/services/${s.slug}`, label: s.title, testId: `service-${s.slug}` }));
  const marketingLinks = marketingServices.map((s) => ({ path: `/services/${s.slug}`, label: s.title, testId: `service-${s.slug}` }));

  const tagline =
    settings.footer_tagline ||
    "We engineer websites, search visibility and paid growth for brands built to lead — across Google, ChatGPT, and everything after.";
  const footerCtaText = settings.footer_cta_text || "Let's build something";
  const footerCtaLink = settings.footer_cta_link || "/contact";
  const copyright = formatCopyright(settings.footer_copyright_text) || `© ${new Date().getFullYear()} Creative Codez. All rights reserved.`;

  return (
    <footer data-testid="site-footer" className="relative border-t border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-[#050506] pt-20 pb-10 overflow-hidden">
      <div className="pointer-events-none absolute -top-32 left-1/3 w-[500px] h-[500px] rounded-full bg-teal-500/10 blur-[120px]" />
      <div className="container-px mx-auto relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 pb-16 border-b border-slate-200 dark:border-white/10">
          <div className="md:col-span-4">
            <div className="mb-6">
              <BrandLogo testId="footer-logo-link" />
            </div>
            <p data-testid="footer-tagline-text" className="text-slate-500 dark:text-slate-400 font-body max-w-sm leading-relaxed">
              {tagline}
            </p>
            <div className="mt-6">
              <SocialIcons links={settings.social_links} />
            </div>
          </div>

          <div className="md:col-span-2">
            <FooterLinkColumn title="Navigate" links={navLinks} testIdPrefix="footer-link" />
          </div>
          <div className="md:col-span-3">
            <FooterLinkColumn title="Services" links={devLinks} testIdPrefix="footer" />
          </div>
          <div className="md:col-span-3">
            <FooterLinkColumn title="Marketing" links={marketingLinks} testIdPrefix="footer" />
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pt-8">
          <div className="flex items-center gap-3 flex-wrap">
            <p data-testid="footer-copyright-text" className="text-slate-400 dark:text-slate-500 text-sm font-body">
              {copyright}
            </p>
            <span className="text-slate-300 dark:text-white/20">·</span>
            <Link
              to="/sitemap"
              data-testid="footer-sitemap-link"
              className="text-slate-400 dark:text-slate-500 text-sm font-body hover:text-teal-500 dark:hover:text-teal-400 transition-colors duration-300"
            >
              Sitemap
            </Link>
            <span className="text-slate-300 dark:text-white/20">·</span>
            <Link
              to="/terms-and-conditions"
              data-testid="footer-terms-link"
              className="text-slate-400 dark:text-slate-500 text-sm font-body hover:text-teal-500 dark:hover:text-teal-400 transition-colors duration-300"
            >
              Terms & Conditions
            </Link>
            <span className="text-slate-300 dark:text-white/20">·</span>
            <Link
              to="/privacy-policy"
              data-testid="footer-privacy-link"
              className="text-slate-400 dark:text-slate-500 text-sm font-body hover:text-teal-500 dark:hover:text-teal-400 transition-colors duration-300"
            >
              Privacy Policy
            </Link>
          </div>
          <Link to={footerCtaLink} data-testid="footer-cta-button" className="inline-flex items-center gap-2 text-sm text-slate-900 dark:text-white group">
            <span className="border-b border-slate-300 dark:border-white/30 group-hover:border-teal-400 transition-colors duration-300">{footerCtaText}</span>
            <ArrowUpRight size={16} className="group-hover:text-teal-400 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
          </Link>
        </div>

        <div className="mt-6">
          <CalendlyButton testId="footer-book-call-button" />
        </div>
      </div>
    </footer>
  );
};
